import type {
  Character,
  Encounter,
  GeneratorSettings,
  Location,
  LockState,
  Objective,
  SideQuest,
  Skill,
  SlotCounts,
  StorySeed,
  StorySeedDrafts,
} from "@/types/story.ts";
import {
  createEmptyDraftsForCounts,
  createEmptySeedForCounts,
  createLockStateForCounts,
  DEFAULT_SETTINGS,
  DEFAULT_SLOT_COUNTS,
} from "@/types/story.ts";
import type { SavedStory } from "@/lib/storage/stories.ts";
import { TEST_STORY_DATA } from "@/lib/data/testData.ts";

export type StoryAction =
  | { type: "load"; story: SavedStory | null }
  | {
    type: "selectSuggestion";
    field: keyof StorySeedDrafts;
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string;
  }
  | {
    type: "removeSuggestion";
    field: keyof StorySeedDrafts;
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string;
  }
  | { type: "toggleLock"; field: keyof LockState; arrayIndex?: number }
  | { type: "lockAll" }
  | { type: "unlockAll" }
  | { type: "updateSettings"; settings: Partial<GeneratorSettings> }
  | { type: "updateStoryName"; name: string }
  | { type: "updateStoryIntro"; storyIntro: string }
  | { type: "updateSlotCount"; field: keyof SlotCounts; delta: number }
  | { type: "updateSideQuest"; slotIndex: number; sideQuest: Partial<SideQuest> }
  | { type: "updateCharacter"; slotIndex: number; character: Partial<Character> }
  | { type: "updateLocation"; slotIndex: number; location: Partial<Location> }
  | { type: "updateSkill"; slotIndex: number; skill: Partial<Skill> }
  | { type: "updateEncounter"; slotIndex: number; encounter: Partial<Encounter> }
  | { type: "updateMainObjective"; objective: Partial<Objective> }
  | { type: "updateStoryTwist"; storyTwist: string }
  | { type: "setDrafts"; drafts: StorySeedDrafts }
  | { type: "setPartialDrafts"; drafts: Partial<StorySeedDrafts> }
  | { type: "clearDrafts"; fields: Array<keyof StorySeedDrafts> }
  | { type: "setStep"; step: 1 | 2 | 3 | 4 }
  | { type: "lockInStep2"; skills: Skill[]; themeVariant: string }
  | { type: "lockInStep3"; objective: Objective }
  | { type: "clearAllSkillUses" }
  | { type: "loadTestData" };

export function reduceStory(
  story: SavedStory | null,
  action: StoryAction,
): SavedStory | null {
  if (action.type === "load") {
    return action.story ? migrateStory(action.story) : null;
  }

  if (!story) return story;

  switch (action.type) {
    case "selectSuggestion":
      return applySelection(story, action.field, action.item);
    case "toggleLock":
      return applyToggleLock(story, action.field, action.arrayIndex);
    case "removeSuggestion":
      return applyRemoveSuggestion(story, action.field, action.item);
    case "lockAll":
      return applyLockAll(story);
    case "unlockAll":
      return applyUnlockAll(story);
    case "updateSettings":
      return {
        ...story,
        settings: { ...story.settings, ...action.settings },
      };
    case "updateStoryName":
      return {
        ...story,
        name: action.name,
        storySeed: { ...story.storySeed, storyName: action.name },
      };
    case "updateStoryIntro":
      return {
        ...story,
        storySeed: {
          ...story.storySeed,
          storyIntro: action.storyIntro,
        },
      };
    case "updateSlotCount":
      return applySlotCountChange(story, action.field, action.delta);
    case "updateSideQuest":
      return updateArrayItem(story, "sideQuests", action.slotIndex, action.sideQuest);
    case "updateCharacter":
      return updateArrayItem(story, "characters", action.slotIndex, action.character);
    case "updateLocation":
      return updateArrayItem(story, "locations", action.slotIndex, action.location);
    case "updateSkill":
      return updateArrayItem(story, "skills", action.slotIndex, action.skill);
    case "updateEncounter":
      return updateArrayItem(story, "encounters", action.slotIndex, action.encounter);
    case "updateMainObjective":
      return {
        ...story,
        storySeed: {
          ...story.storySeed,
          mainObjective: { ...story.storySeed.mainObjective, ...action.objective },
        },
      };
    case "updateStoryTwist":
      return {
        ...story,
        storySeed: {
          ...story.storySeed,
          storyTwist: action.storyTwist,
        },
      };
    case "setDrafts":
      return { ...story, drafts: action.drafts };
    case "setPartialDrafts":
      return applyPartialDrafts(story, action.drafts);
    case "clearDrafts":
      return applyClearDrafts(story, action.fields);
    case "setStep":
      return {
        ...story,
        settings: { ...story.settings, generationStep: action.step },
      };
    case "lockInStep2":
      return {
        ...story,
        settings: {
          ...story.settings,
          lockedSkills: action.skills,
          themeVariant: action.themeVariant,
          generationStep: 3,
        },
        storySeed: {
          ...story.storySeed,
          skills: action.skills,
          mainObjective: {
            ...story.storySeed.mainObjective,
            skillUses: [],
          },
          sideQuests: story.storySeed.sideQuests.map((quest) => ({
            ...quest,
            skillUses: [],
          })),
          characters: story.storySeed.characters.map((character) => ({
            ...character,
            skillUses: [],
          })),
          locations: story.storySeed.locations.map((location) => ({
            ...location,
            skillUses: [],
          })),
          encounters: story.storySeed.encounters.map((encounter) => ({
            ...encounter,
            skillUses: [],
          })),
        },
      };
    case "lockInStep3":
      return {
        ...story,
        storySeed: {
          ...story.storySeed,
          mainObjective: action.objective,
        },
        lockState: {
          ...story.lockState,
          mainObjective: true,
        },
        settings: {
          ...story.settings,
          generationStep: 4,
        },
      };
    case "clearAllSkillUses":
      return {
        ...story,
        storySeed: {
          ...story.storySeed,
          mainObjective: {
            ...story.storySeed.mainObjective,
            skillUses: [],
          },
          sideQuests: story.storySeed.sideQuests.map((quest) => ({
            ...quest,
            skillUses: [],
          })),
          characters: story.storySeed.characters.map((character) => ({
            ...character,
            skillUses: [],
          })),
          locations: story.storySeed.locations.map((location) => ({
            ...location,
            skillUses: [],
          })),
          encounters: story.storySeed.encounters.map((encounter) => ({
            ...encounter,
            skillUses: [],
          })),
        },
      };
    case "loadTestData":
      return applyTestData(story);
    default:
      return story;
  }
}

export function migrateStory(loaded: SavedStory): SavedStory {
  const slotCounts = loaded.settings?.slotCounts || DEFAULT_SLOT_COUNTS;
  const normalizeAgeGroup = (value?: string): string => {
    if (!value) return DEFAULT_SETTINGS.ageGroup;
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) return trimmed;
    if (trimmed === "12+") return "12+";
    if (trimmed === "3-5 years") return "4";
    if (trimmed === "6-8 years") return "7";
    if (trimmed === "9-12 years") return "10";
    return trimmed;
  };

  return {
    ...loaded,
    drafts: {
      ...createEmptyDraftsForCounts(slotCounts),
      ...loaded.drafts, // Preserve existing drafts
    },
    lockState: {
      ...createLockStateForCounts(slotCounts),
      ...loaded.lockState,
      storyName: loaded.lockState?.storyName ?? false,
    },
    storySeed: {
      ...createEmptySeedForCounts(slotCounts),
      ...loaded.storySeed,
      storyName: loaded.storySeed?.storyName ?? "",
      encounters: (loaded.storySeed?.encounters ?? createEmptySeedForCounts(slotCounts).encounters)
        .map(
          (encounter) => ({
            ...encounter,
            skillUses: encounter.skillUses ?? [],
          }),
        ),
    },
    settings: {
      ...DEFAULT_SETTINGS,
      ...loaded.settings,
      slotCounts,
      ageGroup: normalizeAgeGroup(loaded.settings?.ageGroup),
      // Migrate old stories to step 4 (playground) if they have content
      generationStep: loaded.settings?.generationStep ??
        (loaded.storySeed?.storyName || loaded.storySeed?.mainObjective?.description ? 4 : 1),
      themeVariant: loaded.settings?.themeVariant ?? "",
      lockedSkills: loaded.settings?.lockedSkills ?? [],
      mainCharacterName: loaded.settings?.mainCharacterName ?? "",
    },
  };
}

function cloneStorySeed(storySeed: StorySeed): StorySeed {
  return {
    ...storySeed,
    mainObjective: { ...storySeed.mainObjective },
    skills: [...storySeed.skills],
    characters: [...storySeed.characters],
    locations: [...storySeed.locations],
    sideQuests: [...storySeed.sideQuests],
    encounters: [...storySeed.encounters],
  };
}

function cloneLockState(lockState: LockState): LockState {
  return {
    ...lockState,
    skills: [...lockState.skills],
    characters: [...lockState.characters],
    locations: [...lockState.locations],
    sideQuests: [...lockState.sideQuests],
    encounters: [...lockState.encounters],
  };
}

function cloneDrafts(drafts: StorySeedDrafts): StorySeedDrafts {
  return {
    storyName: { options: [...drafts.storyName.options] },
    mainObjective: { options: [...drafts.mainObjective.options] },
    storyTwist: { options: [...drafts.storyTwist.options] },
    storyIntro: { options: [...drafts.storyIntro.options] },
    skills: { options: [...drafts.skills.options] },
    characters: { options: [...drafts.characters.options] },
    locations: { options: [...drafts.locations.options] },
    sideQuests: { options: [...drafts.sideQuests.options] },
    encounters: { options: [...drafts.encounters.options] },
    themeVariant: { options: [...drafts.themeVariant.options] },
  };
}

function applyPartialDrafts(
  story: SavedStory,
  partialDrafts: Partial<StorySeedDrafts>,
): SavedStory {
  const newDrafts = cloneDrafts(story.drafts);

  if (partialDrafts.storyName) newDrafts.storyName = partialDrafts.storyName;
  if (partialDrafts.mainObjective) newDrafts.mainObjective = partialDrafts.mainObjective;
  if (partialDrafts.storyTwist) newDrafts.storyTwist = partialDrafts.storyTwist;
  if (partialDrafts.storyIntro) newDrafts.storyIntro = partialDrafts.storyIntro;
  if (partialDrafts.skills) newDrafts.skills = partialDrafts.skills;
  if (partialDrafts.characters) newDrafts.characters = partialDrafts.characters;
  if (partialDrafts.locations) newDrafts.locations = partialDrafts.locations;
  if (partialDrafts.sideQuests) newDrafts.sideQuests = partialDrafts.sideQuests;
  if (partialDrafts.encounters) newDrafts.encounters = partialDrafts.encounters;
  if (partialDrafts.themeVariant) newDrafts.themeVariant = partialDrafts.themeVariant;

  return { ...story, drafts: newDrafts };
}

function applyClearDrafts(
  story: SavedStory,
  fields: Array<keyof StorySeedDrafts>,
): SavedStory {
  const newDrafts = cloneDrafts(story.drafts);

  for (const field of fields) {
    (newDrafts as Record<keyof StorySeedDrafts, { options: unknown[] }>)[field] = { options: [] };
  }

  return { ...story, drafts: newDrafts };
}

function isEmpty(value: unknown): boolean {
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "object" && value !== null) {
    return Object.values(value).every((v) =>
      typeof v === "string" ? v.trim() === "" : Array.isArray(v) && v.length === 0
    );
  }
  return true;
}

function suggestionKey(
  item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
): string {
  if (typeof item === "string") {
    return `string:${item.trim().toLowerCase()}`;
  }

  if ("name" in item && "role" in item && "emoji" in item) {
    return `character:${item.name.trim().toLowerCase()}|${item.role.trim().toLowerCase()}|${item.emoji.trim()}`;
  }

  if ("name" in item && "description" in item && "emoji" in item) {
    return `location:${item.name.trim().toLowerCase()}|${item.description.trim().toLowerCase()}|${item.emoji.trim()}`;
  }

  if ("name" in item && "emoji" in item) {
    return `skill:${item.name.trim().toLowerCase()}|${item.emoji.trim()}`;
  }

  if ("description" in item && "type" in item) {
    return `encounter:${item.description.trim().toLowerCase()}|${
      (item.type ?? "").trim().toLowerCase()
    }`;
  }

  if ("description" in item) {
    return `description:${item.description.trim().toLowerCase()}`;
  }

  return JSON.stringify(item);
}

function removeOptionByValue<T>(
  options: T[],
  target: Character | Location | Skill | SideQuest | Objective | Encounter | string,
): T[] {
  const targetKey = suggestionKey(target);
  return options.filter((option) =>
    suggestionKey(
      option as Character | Location | Skill | SideQuest | Objective | Encounter | string,
    ) !== targetKey
  );
}

function applySelection(
  story: SavedStory,
  field: keyof StorySeedDrafts,
  item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
): SavedStory {
  const newStorySeed = cloneStorySeed(story.storySeed);
  const newDrafts = cloneDrafts(story.drafts);
  const newLockState = cloneLockState(story.lockState);

  if (field === "storyName" && typeof item === "string") {
    newStorySeed.storyName = item;
    story = { ...story, name: item };
    newLockState.storyName = true;
    newDrafts.storyName = { options: removeOptionByValue(newDrafts.storyName.options, item) };
  } else if (field === "mainObjective" && typeof item === "object" && "description" in item) {
    newStorySeed.mainObjective = item as Objective;
    newLockState.mainObjective = true;
    newDrafts.mainObjective = {
      options: removeOptionByValue(newDrafts.mainObjective.options, item),
    };
  } else if (field === "storyTwist" && typeof item === "string") {
    newStorySeed.storyTwist = item;
    newLockState.storyTwist = true;
    newDrafts.storyTwist = { options: removeOptionByValue(newDrafts.storyTwist.options, item) };
  } else if (field === "storyIntro" && typeof item === "string") {
    newStorySeed.storyIntro = item;
    newLockState.storyIntro = true;
    newDrafts.storyIntro = { options: removeOptionByValue(newDrafts.storyIntro.options, item) };
  } else if (field === "skills") {
    const firstEmptyIndex = newStorySeed.skills.findIndex(isEmpty);
    if (firstEmptyIndex !== -1) {
      newStorySeed.skills[firstEmptyIndex] = item as Skill;
      newLockState.skills[firstEmptyIndex] = true;
      newDrafts.skills = { options: removeOptionByValue(newDrafts.skills.options, item) };
    }
  } else if (field === "characters") {
    const firstEmptyIndex = newStorySeed.characters.findIndex(isEmpty);
    if (firstEmptyIndex !== -1) {
      newStorySeed.characters[firstEmptyIndex] = item as Character;
      newLockState.characters[firstEmptyIndex] = true;
      newDrafts.characters = {
        options: removeOptionByValue(newDrafts.characters.options, item),
      };
    }
  } else if (field === "locations") {
    const firstEmptyIndex = newStorySeed.locations.findIndex(isEmpty);
    if (firstEmptyIndex !== -1) {
      newStorySeed.locations[firstEmptyIndex] = item as Location;
      newLockState.locations[firstEmptyIndex] = true;
      newDrafts.locations = { options: removeOptionByValue(newDrafts.locations.options, item) };
    }
  } else if (field === "sideQuests") {
    const firstEmptyIndex = newStorySeed.sideQuests.findIndex(isEmpty);
    if (firstEmptyIndex !== -1) {
      newStorySeed.sideQuests[firstEmptyIndex] = item as SideQuest;
      newLockState.sideQuests[firstEmptyIndex] = true;
      newDrafts.sideQuests = {
        options: removeOptionByValue(newDrafts.sideQuests.options, item),
      };
    }
  } else if (field === "encounters") {
    const firstEmptyIndex = newStorySeed.encounters.findIndex(isEmpty);
    if (firstEmptyIndex !== -1) {
      newStorySeed.encounters[firstEmptyIndex] = item as Encounter;
      newLockState.encounters[firstEmptyIndex] = true;
      newDrafts.encounters = {
        options: removeOptionByValue(newDrafts.encounters.options, item),
      };
    }
  }

  return { ...story, storySeed: newStorySeed, drafts: newDrafts, lockState: newLockState };
}

function applyRemoveSuggestion(
  story: SavedStory,
  field: keyof StorySeedDrafts,
  item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
): SavedStory {
  const newDrafts = cloneDrafts(story.drafts);

  if (field === "storyName" && typeof item === "string") {
    newDrafts.storyName = { options: removeOptionByValue(newDrafts.storyName.options, item) };
  } else if (field === "mainObjective" && typeof item === "object" && "description" in item) {
    newDrafts.mainObjective = {
      options: removeOptionByValue(newDrafts.mainObjective.options, item),
    };
  } else if (field === "storyTwist" && typeof item === "string") {
    newDrafts.storyTwist = { options: removeOptionByValue(newDrafts.storyTwist.options, item) };
  } else if (field === "storyIntro" && typeof item === "string") {
    newDrafts.storyIntro = { options: removeOptionByValue(newDrafts.storyIntro.options, item) };
  } else if (field === "themeVariant" && typeof item === "string") {
    newDrafts.themeVariant = {
      options: removeOptionByValue(newDrafts.themeVariant.options, item),
    };
  } else if (field === "skills") {
    newDrafts.skills = { options: removeOptionByValue(newDrafts.skills.options, item) };
  } else if (field === "characters") {
    newDrafts.characters = { options: removeOptionByValue(newDrafts.characters.options, item) };
  } else if (field === "locations") {
    newDrafts.locations = { options: removeOptionByValue(newDrafts.locations.options, item) };
  } else if (field === "sideQuests") {
    newDrafts.sideQuests = { options: removeOptionByValue(newDrafts.sideQuests.options, item) };
  } else if (field === "encounters") {
    newDrafts.encounters = { options: removeOptionByValue(newDrafts.encounters.options, item) };
  }

  return { ...story, drafts: newDrafts };
}

function applyToggleLock(
  story: SavedStory,
  field: keyof LockState,
  arrayIndex?: number,
): SavedStory {
  const newLockState = cloneLockState(story.lockState);
  const newDrafts = cloneDrafts(story.drafts);
  const newStorySeed = cloneStorySeed(story.storySeed);

  if (arrayIndex !== undefined) {
    const array = newLockState[field] as boolean[];
    const wasLocked = array[arrayIndex];
    array[arrayIndex] = !array[arrayIndex];

    if (wasLocked && !array[arrayIndex]) {
      if (field === "skills") {
        const item = newStorySeed.skills[arrayIndex];
        if (!isEmpty(item)) {
          newDrafts.skills = { options: [...newDrafts.skills.options, item] };
          newStorySeed.skills[arrayIndex] = { name: "", emoji: "" };
        }
      } else if (field === "characters") {
        const item = newStorySeed.characters[arrayIndex];
        if (!isEmpty(item)) {
          newDrafts.characters = { options: [...newDrafts.characters.options, item] };
          newStorySeed.characters[arrayIndex] = { name: "", role: "", emoji: "", skillUses: [] };
        }
      } else if (field === "locations") {
        const item = newStorySeed.locations[arrayIndex];
        if (!isEmpty(item)) {
          newDrafts.locations = { options: [...newDrafts.locations.options, item] };
          newStorySeed.locations[arrayIndex] = {
            name: "",
            description: "",
            emoji: "",
            skillUses: [],
          };
        }
      } else if (field === "sideQuests") {
        const item = newStorySeed.sideQuests[arrayIndex];
        if (!isEmpty(item)) {
          newDrafts.sideQuests = { options: [...newDrafts.sideQuests.options, item] };
          newStorySeed.sideQuests[arrayIndex] = { description: "", skillUses: [] };
        }
      } else if (field === "encounters") {
        const item = newStorySeed.encounters[arrayIndex];
        if (!isEmpty(item)) {
          newDrafts.encounters = { options: [...newDrafts.encounters.options, item] };
          newStorySeed.encounters[arrayIndex] = { description: "", skillUses: [] };
        }
      }
    }
  } else {
    const wasLocked = newLockState[field] as boolean;
    (newLockState[field] as boolean) = !wasLocked;

    if (wasLocked) {
      if (field === "storyName" && newStorySeed.storyName) {
        newDrafts.storyName = { options: [...newDrafts.storyName.options, newStorySeed.storyName] };
        newStorySeed.storyName = "";
      } else if (field === "mainObjective" && newStorySeed.mainObjective.description) {
        newDrafts.mainObjective = {
          options: [...newDrafts.mainObjective.options, newStorySeed.mainObjective],
        };
        newStorySeed.mainObjective = { description: "", skillUses: [] };
      } else if (field === "storyTwist" && newStorySeed.storyTwist) {
        newDrafts.storyTwist = {
          options: [...newDrafts.storyTwist.options, newStorySeed.storyTwist],
        };
        newStorySeed.storyTwist = "";
      } else if (field === "storyIntro" && newStorySeed.storyIntro) {
        newDrafts.storyIntro = {
          options: [...newDrafts.storyIntro.options, newStorySeed.storyIntro],
        };
        newStorySeed.storyIntro = "";
      }
    }
  }

  return { ...story, lockState: newLockState, drafts: newDrafts, storySeed: newStorySeed };
}

function applyLockAll(story: SavedStory): SavedStory {
  const newLockState: LockState = {
    storyName: true,
    mainObjective: true,
    storyTwist: true,
    storyIntro: true,
    skills: story.lockState.skills.map(() => true),
    characters: story.lockState.characters.map(() => true),
    locations: story.lockState.locations.map(() => true),
    sideQuests: story.lockState.sideQuests.map(() => true),
    encounters: story.lockState.encounters.map(() => true),
  };

  return { ...story, lockState: newLockState };
}

function applyUnlockAll(story: SavedStory): SavedStory {
  const newLockState: LockState = {
    storyName: false,
    mainObjective: false,
    storyTwist: false,
    storyIntro: false,
    skills: story.lockState.skills.map(() => false),
    characters: story.lockState.characters.map(() => false),
    locations: story.lockState.locations.map(() => false),
    sideQuests: story.lockState.sideQuests.map(() => false),
    encounters: story.lockState.encounters.map(() => false),
  };

  return { ...story, lockState: newLockState };
}

function applySlotCountChange(
  story: SavedStory,
  field: keyof SlotCounts,
  delta: number,
): SavedStory {
  const slotCounts = { ...story.settings.slotCounts };
  const newCount = Math.max(1, Math.min(7, slotCounts[field] + delta));
  slotCounts[field] = newCount;

  const newSettings = { ...story.settings, slotCounts };

  const resizeArray = <T>(arr: T[], newSize: number, emptyValue: T): T[] => {
    if (arr.length > newSize) return arr.slice(0, newSize);
    return [...arr, ...Array.from({ length: newSize - arr.length }, () => emptyValue)];
  };

  const newStorySeed = cloneStorySeed(story.storySeed);
  const newLockState = cloneLockState(story.lockState);
  const newDrafts = {
    ...story.drafts,
    sideQuests: { options: [...story.drafts.sideQuests.options] },
    encounters: { options: [...story.drafts.encounters.options] },
    characters: { options: [...story.drafts.characters.options] },
    locations: { options: [...story.drafts.locations.options] },
  };

  if (field === "sideQuests") {
    if (newCount < newStorySeed.sideQuests.length) {
      const removed = newStorySeed.sideQuests
        .slice(newCount)
        .filter((quest) => quest.description.trim().length > 0);
      if (removed.length > 0) {
        newDrafts.sideQuests = {
          options: [...newDrafts.sideQuests.options, ...removed],
        };
      }
    }

    newStorySeed.sideQuests = resizeArray(newStorySeed.sideQuests, newCount, {
      description: "",
      skillUses: [],
    });
    newLockState.sideQuests = resizeArray(newLockState.sideQuests, newCount, false);
  } else if (field === "encounters") {
    if (newCount < newStorySeed.encounters.length) {
      const removed = newStorySeed.encounters
        .slice(newCount)
        .filter((encounter) => encounter.description.trim().length > 0);
      if (removed.length > 0) {
        newDrafts.encounters = {
          options: [...newDrafts.encounters.options, ...removed],
        };
      }
    }

    newStorySeed.encounters = resizeArray(newStorySeed.encounters, newCount, {
      description: "",
      skillUses: [],
    });
    newLockState.encounters = resizeArray(newLockState.encounters, newCount, false);
  } else if (field === "characters") {
    if (newCount < newStorySeed.characters.length) {
      const removed = newStorySeed.characters
        .slice(newCount)
        .filter((character) => character.name.trim().length > 0);
      if (removed.length > 0) {
        newDrafts.characters = {
          options: [...newDrafts.characters.options, ...removed],
        };
      }
    }

    newStorySeed.characters = resizeArray(newStorySeed.characters, newCount, {
      name: "",
      role: "",
      emoji: "",
      skillUses: [],
    });
    newLockState.characters = resizeArray(newLockState.characters, newCount, false);
  } else if (field === "locations") {
    if (newCount < newStorySeed.locations.length) {
      const removed = newStorySeed.locations
        .slice(newCount)
        .filter((location) => location.name.trim().length > 0);
      if (removed.length > 0) {
        newDrafts.locations = {
          options: [...newDrafts.locations.options, ...removed],
        };
      }
    }

    newStorySeed.locations = resizeArray(newStorySeed.locations, newCount, {
      name: "",
      description: "",
      emoji: "",
      skillUses: [],
    });
    newLockState.locations = resizeArray(newLockState.locations, newCount, false);
  }

  return {
    ...story,
    settings: newSettings,
    storySeed: newStorySeed,
    lockState: newLockState,
    drafts: newDrafts,
  };
}

function updateArrayItem<
  K extends "sideQuests" | "characters" | "locations" | "skills" | "encounters",
>(
  story: SavedStory,
  field: K,
  slotIndex: number,
  update: Partial<StorySeed[K][number]>,
): SavedStory {
  const newStorySeed = cloneStorySeed(story.storySeed);
  const newItems = [...newStorySeed[field]] as StorySeed[K];
  newItems[slotIndex] = { ...newItems[slotIndex], ...update } as StorySeed[K][number];
  newStorySeed[field] = newItems;

  return { ...story, storySeed: newStorySeed };
}

function applyTestData(story: SavedStory): SavedStory {
  const newStorySeed = { ...TEST_STORY_DATA };
  const newLockState: LockState = {
    storyName: true,
    mainObjective: true,
    storyTwist: true,
    storyIntro: true,
    skills: [true, true, true, true],
    characters: [true, true, true, true, true],
    locations: [true, true, true, true, true],
    sideQuests: [true, true, true],
    encounters: [true, true, true],
  };

  const newDrafts = createEmptyDraftsForCounts(story.settings.slotCounts);

  return {
    ...story,
    storySeed: newStorySeed,
    lockState: newLockState,
    drafts: newDrafts,
  };
}
